import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  DEFAULT_CACHE_ID,
  DEFAULT_VOLUME,
  LSKeys,
  Volume,
  useLocalStorage,
} from '@outdoria/ui';

type ContextType = {
  isLoggedIn: boolean;
  setIsLoggedIn: (isLoggedIn: boolean) => void;
  bearerToken: { bearer: string | null };
  setBearerToken: (bearerToken: { bearer: string | null }) => void;
  // MUSIC
  music: string | undefined;
  setMusic: (music: string | undefined) => void;
  musicVolume: number;
  setMusicVolume: (musicVolume: number) => void;
  isMusicPlaying: boolean;
  setIsMusicPlaying: (isMusicPlaying: boolean) => void;
  // CACHING
  cacheId: { id: string };
  setCacheId: (cacheId: { id: string }) => void;
  // CHOOSING ADVENTURE
  adventure: { id: string };
  setAdventure: (adventure: { id: string }) => void;
};

export const AppContextProvider = ({
  children,
}: PropsWithChildren): JSX.Element => {
  const [volume] = useLocalStorage<Record<Volume, number>>(
    LSKeys.OUTDORIA_VOLUME,
    DEFAULT_VOLUME,
  );
  const [bearerToken, setBearerToken] = useLocalStorage<{
    bearer: string | null;
  }>(LSKeys.OUTDORIA_AUTH, {
    bearer: null,
  });
  const [adventure, setAdventure] = useLocalStorage<{
    id: string;
  }>(LSKeys.OUTDORIA_ADVENTURE, {
    id: 'outdoria',
  });
  const [cacheId, setCacheId] = useLocalStorage<{
    id: string;
  }>(LSKeys.OUTDORIA_CACHE_ID, DEFAULT_CACHE_ID);

  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(
    Boolean(bearerToken.bearer),
  );

  useEffect(() => {
    setIsLoggedIn(Boolean(bearerToken.bearer));
  }, [bearerToken.bearer]);

  useEffect(() => {
    if (!cacheId?.id) {
      setCacheId({ id: String(Date.now()) });
    }
  }, [cacheId]);

  const [music, setMusic] = useState<string>();
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  // TODO maybe this can be done somewhere else
  // since we use the same algorithm in useSound
  const [musicVolume, setMusicVolume] = useState<number>(
    (volume.bgm / 100) * (volume.master / 100),
  );

  const value = {
    isLoggedIn,
    setIsLoggedIn,
    bearerToken,
    setBearerToken,
    // CACHING
    cacheId,
    setCacheId,
    // ADVENTURE
    adventure,
    setAdventure,
    // MUSIC
    music,
    setMusic,
    isMusicPlaying,
    setIsMusicPlaying,
    musicVolume,
    setMusicVolume,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const AppContext = React.createContext({} as ContextType);
export const useAppContext = (): ContextType => useContext(AppContext);
